"use client";

import { useDebouncedCallback } from "@tanstack/react-pacer";
import RangeSlider from "@/components/frontend/ui/form/inputs/range-slider";
import RangeText from "@/components/frontend/ui/form/inputs/range-text";
import type { FilterRange, FilterValue } from "@/lib/types/marketsuite/datatypes/filters";
import type { MsPriceFilterBlock } from "@/payload-types";
import type { AnyFieldApi } from "@tanstack/react-form-nextjs";
import { RangeFilterDefinition } from "../schemas/range";

export type OptionsRangeFilterBlock = Omit<MsPriceFilterBlock, "blockType" | "id" | "blockName">;
export interface MsRangeFilterProps extends OptionsRangeFilterBlock {
	blockType: RangeFilterDefinition["slug"];
	field: AnyFieldApi;
	filterData?: FilterRange;
	onValueChange?: (value: FilterValue, blockType: RangeFilterDefinition["slug"]) => void;
}

function clamp(value: number, min: number, max: number) {
	return Math.min(Math.max(value, min), max);
}

export const MsRangeFilter: React.FC<MsRangeFilterProps> = ({ blockType, field, onValueChange, content, appearance, settings, filterData }) => {
	const debouncedValueChange = useDebouncedCallback((next: number | readonly number[]) => onValueChange?.(next, blockType), { wait: 400 });

	if (!content?.label) return null;

	const dynamic = settings?.dynamic !== false;
	const needsDynamicData = content.min == null || content.max == null;
	if (dynamic && needsDynamicData && !filterData) return null;

	const min = content.min ?? filterData?.start ?? 1;
	const max = content.max ?? filterData?.end ?? 100;

	const defaultMin = clamp(content.defaultMin ?? min, min, max);
	const defaultMax = clamp(content.defaultMax ?? max, min, max);

	const defaultValue = content.mode === "range" ? [defaultMin, defaultMax] : clamp(content.default ?? min, min, max);

	const value = field.state.value ?? defaultValue;

	const handleValueChange = (next: number | readonly number[]) => {
		field.handleChange(next);
		onValueChange?.(next, blockType);
	};

	if (appearance?.inputType === "range-text") {
		return (
			<RangeText
				id={field.name}
				name={field.name}
				label={content.label}
				description={content.description}
				mode={content.mode}
				value={value}
				onValueChange={handleValueChange}
				onBlur={field.handleBlur}
				min={min}
				max={max}
				placeholder={content.placeholder}
				minPlaceholder={content.minPlaceholder}
				maxPlaceholder={content.maxPlaceholder}
			/>
		);
	}

	return (
		<RangeSlider
			id={field.name}
			name={field.name}
			label={content.label}
			description={content.description}
			value={value}
			defaultValue={defaultValue}
			onValueChange={(next) => {
				field.handleChange(next);
				debouncedValueChange(next);
			}}
			onBlur={field.handleBlur}
			min={min}
			max={max}
			step={content.step || undefined}
		/>
	);
};
